// AnimatedIdlePokemon.tsx
import { S3_BASE_URL } from "@/utils/s3";
import { loadAnimXML, AnimSpec } from "@/utils/sprites/sheet-cut";
import { getPokemonSpriteScale } from "game/data/pokemon/sprite-scale-overrides";
import { Pokemon } from "prisma-layer";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";
import React, { useEffect, useState, useRef, useMemo, memo } from "react";
import { Direction, DirectionArray } from "types/battle-engine/battle";

// Cache for XML animation data (shared across all instances)
const xmlCache = new Map<string, Promise<Record<string, AnimSpec>>>();

// Cache for loaded images (shared across all instances)
const imageCache = new Map<string, HTMLImageElement>();

/**
 * Transforms RGB colors in shadow sprite to black with varying opacity
 * Green (0,255,0) → Black 100%
 * Red (255,0,0) → Black 65%
 * Blue (0,0,255) → Black 30%
 */
function processShadowImage(sourceImage: HTMLImageElement): HTMLImageElement {
  const canvas = document.createElement("canvas");
  canvas.width = sourceImage.width;
  canvas.height = sourceImage.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return sourceImage;

  ctx.drawImage(sourceImage, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip transparent pixels
    if (a === 0) continue;

    // Pure green → black 100% opacity
    if (g === 255 && r === 0 && b === 0) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 255;
    }
    // Pure red → black 65% opacity
    else if (r === 255 && g === 0 && b === 0) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = Math.round(255 * 0.65);
    }
    // Pure blue → black 30% opacity
    else if (b === 255 && r === 0 && g === 0) {
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = Math.round(255 * 0.3);
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const processedImage = new Image();
  processedImage.src = canvas.toDataURL();
  return processedImage;
}

// Global animation frame scheduler for all sprites
let rafId: number | null = null;
const animatedSprites = new Set<() => void>();

function scheduleAnimationFrame() {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(() => {
    rafId = null;
    animatedSprites.forEach((update) => update());
    if (animatedSprites.size > 0) {
      scheduleAnimationFrame();
    }
  });
}

export type PokemonSpriteProps = {
  pokemon: {
    base_pokemon_name: string;
    is_shiny?: boolean;
  };
  animation?: string;
  direction?: Direction;
  showShadow?: boolean;
  className?: string;
  style?: React.CSSProperties;
  flipHorizontal?: boolean;
  ignoreScale?: boolean;
};

export const PokemonSprite = memo(
  ({
    pokemon,
    animation = "Idle",
    direction = Direction.SE,
    showShadow = false,
    className,
    style,
    flipHorizontal = false,
    ignoreScale = false,
  }: PokemonSpriteProps) => {
    const [spriteData, setSpriteData] = useState<{
      src: string;
      frameWidth: number;
      frameHeight: number;
      frames: number;
      row: number;
      totalRows: number;
      durations: number[];
    } | null>(null);
    const [shadowSpriteData, setShadowSpriteData] = useState<{
      src: string;
      frameWidth: number;
      frameHeight: number;
      frames: number;
      row: number;
      totalRows: number;
      durations: number[];
    } | null>(null);
    const [frameKey, setFrameKey] = useState(0); // Force re-render on frame change

    const currentFrameRef = useRef<number>(0);
    const frameTimeRef = useRef<number>(0);
    const lastUpdateRef = useRef<number>(0);
    const isAnimatingRef = useRef<boolean>(false);

    useEffect(() => {
      // Create AbortController for cleanup
      const abortController = new AbortController();

      const initAnimatedPokeSprite = async () => {
        // Transform pokemon name for S3 paths (e.g., "charizard-mega-x" -> "charizard__mega-x")
        let pokemonName = parsePokeNameForSprite(pokemon.base_pokemon_name);

        if (pokemon.is_shiny) {
          pokemonName += "__shiny";
        }

        const xmlUrl = `${S3_BASE_URL}/pokemons/${pokemonName}/AnimData.xml`;
        const imageUrl = `${S3_BASE_URL}/pokemons/${pokemonName}/${animation}-Anim.png`;
        const shadowImageUrl = `${S3_BASE_URL}/pokemons/${pokemonName}/${animation}-Shadow.png`;

        // Check cache for XML data
        let animsPromise = xmlCache.get(xmlUrl);
        if (!animsPromise) {
          animsPromise = loadAnimXML(xmlUrl);
          xmlCache.set(xmlUrl, animsPromise);
        }

        const newAnims = await animsPromise;
        const targetAnim = newAnims[animation];
        if (!targetAnim) {
          console.error(`${animation} animation not found for ${pokemonName}!`);
          return;
        }

        // Sleep animation has only 1 row, always use row 0
        const isSleepAnimation = animation === "Sleep";
        const rowIndex = isSleepAnimation
          ? 0
          : DirectionArray.indexOf(direction);
        const totalRows = isSleepAnimation ? 1 : 8;

        const spriteConfig = {
          frameWidth: targetAnim.frameWidth,
          frameHeight: targetAnim.frameHeight,
          frames: targetAnim.durations.length,
          row: rowIndex >= 0 ? rowIndex : 0,
          totalRows: totalRows,
          durations: targetAnim.durations,
        };

        // Load main sprite
        let spriteImage = imageCache.get(imageUrl);
        if (!spriteImage) {
          spriteImage = new Image();
          spriteImage.src = imageUrl;
          imageCache.set(imageUrl, spriteImage);
        }

        const setSpriteWhenReady = () => {
          if (abortController.signal.aborted) return; // Don't update if aborted
          currentFrameRef.current = 0;
          frameTimeRef.current = 0;
          lastUpdateRef.current = performance.now();
          setSpriteData({
            src: spriteImage!.src,
            ...spriteConfig,
          });
        };

        if (spriteImage.complete) {
          setSpriteWhenReady();
        } else {
          spriteImage.addEventListener("load", setSpriteWhenReady, {
            signal: abortController.signal,
          });
        }

        // Load shadow sprite if enabled
        if (showShadow) {
          const processedShadowUrl = `${shadowImageUrl}_processed`;
          let shadowImage = imageCache.get(processedShadowUrl);

          if (!shadowImage) {
            // Load raw shadow image first
            const rawShadowImage = new Image();
            rawShadowImage.crossOrigin = "anonymous";
            rawShadowImage.src = shadowImageUrl;

            rawShadowImage.addEventListener(
              "load",
              () => {
                if (abortController.signal.aborted) return;
                // Process the shadow image to convert RGB to black shades
                const processedImage = processShadowImage(rawShadowImage);
                imageCache.set(processedShadowUrl, processedImage);

                const setShadowWhenReady = () => {
                  if (abortController.signal.aborted) return;
                  setShadowSpriteData({
                    src: processedImage.src,
                    ...spriteConfig,
                  });
                };

                if (processedImage.complete) {
                  setShadowWhenReady();
                } else {
                  processedImage.addEventListener("load", setShadowWhenReady, {
                    signal: abortController.signal,
                  });
                }
              },
              { signal: abortController.signal },
            );
          } else {
            // Use cached processed shadow
            if (shadowImage.complete) {
              setShadowSpriteData({
                src: shadowImage.src,
                ...spriteConfig,
              });
            } else {
              shadowImage.addEventListener(
                "load",
                () => {
                  if (abortController.signal.aborted) return;
                  setShadowSpriteData({
                    src: shadowImage.src,
                    ...spriteConfig,
                  });
                },
                { signal: abortController.signal },
              );
            }
          }
        } else {
          setShadowSpriteData(null);
        }
      };

      initAnimatedPokeSprite();

      // Cleanup function
      return () => {
        abortController.abort();
      };
    }, [
      pokemon.base_pokemon_name,
      pokemon.is_shiny,
      animation,
      direction,
      showShadow,
    ]);

    // Animation update function (called by global RAF)
    const updateAnimation = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
      if (!spriteData) {
        if (updateAnimation.current) {
          animatedSprites.delete(updateAnimation.current);
          updateAnimation.current = undefined;
          isAnimatingRef.current = false;
        }
        return;
      }

      const { frames, durations } = spriteData;

      updateAnimation.current = () => {
        const now = performance.now();
        const delta = now - lastUpdateRef.current;
        const currentFrame = currentFrameRef.current;
        const currentDuration = durations[currentFrame] ?? 16;
        const frameDurationMs = currentDuration * 16; // Convert ticks to ms

        frameTimeRef.current += delta;
        lastUpdateRef.current = now;

        if (frameTimeRef.current >= frameDurationMs) {
          frameTimeRef.current = 0;
          const nextFrame = (currentFrame + 1) % frames;
          currentFrameRef.current = nextFrame;
          // Force re-render by updating frame key
          setFrameKey((prev) => prev + 1);
        }
      };

      animatedSprites.add(updateAnimation.current);
      isAnimatingRef.current = true;
      lastUpdateRef.current = performance.now();
      scheduleAnimationFrame();

      return () => {
        if (updateAnimation.current) {
          animatedSprites.delete(updateAnimation.current);
          updateAnimation.current = undefined;
          isAnimatingRef.current = false;
        }
      };
    }, [spriteData]);

    const configScale = useMemo(
      () => getPokemonSpriteScale(pokemon.base_pokemon_name),
      [pokemon.base_pokemon_name],
    );

    // Merge config scale with any external scale from style prop
    const mergedStyle = useMemo(() => {
      const externalScale = typeof style?.scale === "number" ? style.scale : 1;
      const finalScale = ignoreScale
        ? externalScale
        : configScale * externalScale;
      const { scale: _, ...restStyle } = style ?? {};

      const scaleX = flipHorizontal ? -finalScale : finalScale;
      const scaleY = finalScale;

      return {
        width: (spriteData?.frameWidth ?? 0) * configScale,
        height: (spriteData?.frameHeight ?? 0) * configScale,
        ...restStyle,
        ...(scaleX !== 1 || scaleY !== 1
          ? { scale: `${scaleX} ${scaleY}` }
          : {}),
      };
    }, [
      style,
      configScale,
      spriteData?.frameWidth,
      spriteData?.frameHeight,
      flipHorizontal,
    ]);

    if (!spriteData) {
      return (
        <div className="w-full h-full bg-pk-black/30 rounded-xl p-2 pb-4 animate-pulse" />
      );
    }

    return (
      <div className={`relative ${className ?? ""}`} style={mergedStyle}>
        {/* Shadow layer */}
        {showShadow && shadowSpriteData && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: shadowSpriteData.frameWidth * configScale,
              height: shadowSpriteData.frameHeight * configScale,
              backgroundImage: `url(${shadowSpriteData.src})`,
              backgroundSize: `${
                shadowSpriteData.frameWidth *
                shadowSpriteData.frames *
                configScale
              }px ${
                shadowSpriteData.frameHeight *
                shadowSpriteData.totalRows *
                configScale
              }px`,
              backgroundPosition: `-${
                currentFrameRef.current *
                shadowSpriteData.frameWidth *
                configScale
              }px -${
                shadowSpriteData.row *
                shadowSpriteData.frameHeight *
                configScale
              }px`,
              imageRendering: "pixelated",
            }}
            aria-hidden
          />
        )}
        {/* Main sprite layer */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: spriteData.frameWidth * configScale,
            height: spriteData.frameHeight * configScale,
            backgroundImage: `url(${spriteData.src})`,
            backgroundSize: `${
              spriteData.frameWidth * spriteData.frames * configScale
            }px ${
              spriteData.frameHeight * spriteData.totalRows * configScale
            }px`,
            backgroundPosition: `-${
              currentFrameRef.current * spriteData.frameWidth * configScale
            }px -${spriteData.row * spriteData.frameHeight * configScale}px`,
            imageRendering: "pixelated",
          }}
          aria-hidden
        />
      </div>
    );
  },
);
